import { Diamond } from '@components/Diamond'
import { Link, useParams } from '@tanstack/react-router'
import { cn } from '@utils/cn'
import { useTranslation } from 'react-i18next'

import { GAME_META, GAMES, isGame } from '@/types/game'

const NAV_LINK_CLASSES =
  'pb-1 font-display text-xs tracking-wide-label text-frost/75 uppercase transition-colors ' +
  'hover:text-arcane-bright data-[status=active]:border-b-2 data-[status=active]:border-arcane/60 ' +
  'data-[status=active]:text-arcane-bright'

const GAME_CHIP_CLASSES =
  'rounded-sm border px-2 py-1 font-display text-2xs font-bold tracking-label uppercase transition-colors'

/**
 * The fixed site bar: brand mark (ᚱ diamond + duotone wordmark), the main nav links and the
 * game switcher (the multi-game spine surfaces here — each chip enters that game's codex).
 */
export function NavBar() {
  const { t } = useTranslation()
  const params = useParams({ strict: false })
  const activeGame = isGame(params.game) ? params.game : null

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-steel/15 bg-abyss/70 backdrop-blur-md">
      <nav
        aria-label={t('nav.label')}
        className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 sm:px-8"
      >
        <Link to="/" className="flex items-center gap-3 text-bright hover:text-bright">
          <Diamond rune="ᚱ" size="md" solid />
          <span className="font-display font-bold tracking-brand">
            {t('brand.rune')}
            <span className="text-arcane">{t('brand.ledger')}</span>
          </span>
        </Link>
        <div className="flex items-center gap-5 sm:gap-7">
          <Link to="/" activeOptions={{ exact: true }} className={NAV_LINK_CLASSES}>
            {t('nav.home')}
          </Link>
          <Link to="/sobre" className={NAV_LINK_CLASSES}>
            {t('nav.about')}
          </Link>
          <div className="flex items-center gap-1.5">
            {GAMES.map(game => (
              <Link
                key={game}
                to="/$game/jogador"
                params={{ game }}
                className={cn(
                  GAME_CHIP_CLASSES,
                  activeGame === game
                    ? 'border-arcane/60 bg-arcane/10 text-arcane-bright'
                    : 'border-steel/25 text-mist/70 hover:border-arcane/50 hover:text-arcane-bright'
                )}
              >
                {GAME_META[game].shortName}
              </Link>
            ))}
          </div>
        </div>
      </nav>
    </header>
  )
}
